<script setup lang="ts">
/**
 * База знаний — макет 22:2: страница читается в колонке 940, дерево живёт в
 * сайдбаре, связанные задачи стоят чипами под текстом.
 *
 * Текст показывает тот же редактор, что и описание задачи, только read-only:
 * страница написана таким же markdown, и второй способ его отрисовать разошёлся
 * бы с первым на первом же нестандартном блоке.
 *
 * Обсуждения страницы из макета нет: комментариев у страницы в API не
 * существует (docs/design-gaps.md). История версий — DEV-1481.
 */
import { AppRichTextEditor } from '@vinprop/design-system/editor'
import { AppButton, AppSelect, useToast } from '@vinprop/design-system'
import { AlertTriangle, Book } from 'lucide-vue-next'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import { errorMessage } from '@/api/errors'
import TrackerEmptyState from '@/components/TrackerEmptyState.vue'
import TrackerToolbar from '@/components/TrackerToolbar.vue'
import { pathTo } from '@/features/knowledge/api'
import KbIssues from '@/features/knowledge/components/KbIssues.vue'
import NewSpaceDialog from '@/features/knowledge/components/NewSpaceDialog.vue'
import { useKnowledgeStore } from '@/features/knowledge/store'
import { formatStamp } from '@/features/issues/format'

const { t, locale } = useI18n()
const route = useRoute()
const router = useRouter()
const store = useKnowledgeStore()
const toast = useToast()

// Отказ привязки — тост, а не пустой экран: сама страница читается и без связей.
const onFailed = (failure: unknown) => {
  toast.error(errorMessage(failure, t('kb.issues.failed')))
}

const pageId = computed(() => String(route.params.pageId ?? ''))

const spaceOptions = computed(() =>
  store.spaces.map((space) => ({ value: space.id, label: `${space.name} · ${space.key}` })),
)

/** Крошки страницы — путь по дереву; последняя не ссылка, это текущая. */
const crumbs = computed(() => {
  const path = store.page ? pathTo(store.pages, store.page.id) : []

  return [
    { label: t('nav.knowledge'), to: '/kb' },
    ...path.map((entry, index) => ({
      label: entry.title,
      to: index === path.length - 1 ? undefined : `/kb/${entry.id}`,
    })),
  ]
})

const meta = computed(() => {
  const page = store.page

  if (!page) return ''

  const when = formatStamp(page.updatedAt, locale.value, {
    today: t('issue.today'),
    yesterday: t('issue.yesterday'),
  })

  return [when && t('kb.updated', { when }), t('kb.version', { version: page.version })]
    .filter(Boolean)
    .join(' · ')
})

const isSpaceOpen = ref(false)
const isCreating = ref(false)

/** Первая страница пространства: заводим и сразу открываем в правке. */
const createFirstPage = async () => {
  isCreating.value = true

  try {
    const page = await store.create('', t('kb.tree.newPageTitle'))

    await router.push(`/kb/${page.id}/edit`)
  } finally {
    isCreating.value = false
  }
}

const onSpaceCreated = async (id: string) => {
  isSpaceOpen.value = false
  await store.ensureSpaces()
  await store.selectSpace(id)
}

const onSpace = async (id: string) => {
  await store.selectSpace(id)
  // Пространство сменили — открытая страница осталась в чужом дереве.
  if (pageId.value) await router.push('/kb')
}

const open = async () => {
  await store.ensureSpaces()

  if (pageId.value) await store.openPage(pageId.value)
}

watch(pageId, (id) => {
  if (id) void store.openPage(id)
})

onMounted(open)
</script>

<template>
  <TrackerToolbar :breadcrumbs="crumbs">
    <AppSelect
      v-if="spaceOptions.length > 1"
      :model-value="store.spaceId"
      :options="spaceOptions"
      size="small"
      :aria-label="t('kb.space')"
      @update:model-value="onSpace(String($event))"
    />

    <AppButton v-if="store.page" variant="secondary" size="small" @click="router.push(`/kb/${store.page.id}/edit`)">
      {{ t('kb.edit.action') }}
    </AppButton>
  </TrackerToolbar>

  <div class="kb">
    <div v-if="store.isLoading && !store.page" class="kb__skeleton" />

    <TrackerEmptyState
      v-else-if="store.error"
      :icon="AlertTriangle"
      :title="t('kb.error.title')"
      :description="t('kb.error.description')"
      tint="var(--color-danger-tint)"
    >
      <AppButton variant="secondary" @click="open">{{ t('devops.envs.retry') }}</AppButton>
    </TrackerEmptyState>

    <TrackerEmptyState
      v-else-if="!store.spaces.length"
      :icon="Book"
      :title="t('kb.empty.title')"
      :description="t('kb.empty.description')"
    >
      <AppButton variant="primary" @click="isSpaceOpen = true">{{ t('kb.newSpace.create') }}</AppButton>
    </TrackerEmptyState>

    <!-- Пространство есть, а страниц нет: звать писать первую, а не выбирать
         в пустом дереве (макет 25:2). -->
    <TrackerEmptyState
      v-else-if="!store.pages.length"
      :icon="Book"
      :title="t('kb.blank.title')"
      :description="t('kb.blank.description')"
    >
      <AppButton variant="primary" :loading="isCreating" @click="createFirstPage">
        {{ t('kb.blank.create') }}
      </AppButton>
    </TrackerEmptyState>

    <TrackerEmptyState
      v-else-if="!store.page"
      :icon="Book"
      :title="t('kb.pick.title')"
      :description="t('kb.pick.description')"
    />

    <article v-else class="kb__page">
      <h1 class="kb__title">{{ store.page.title }}</h1>
      <p class="kb__meta">{{ meta }}</p>

      <AppRichTextEditor
        :model-value="store.page.contentMd"
        format="markdown"
        readonly
        preset="issue"
      />

      <KbIssues @failed="onFailed" />
    </article>
  </div>

  <NewSpaceDialog
    v-if="isSpaceOpen"
    @done="onSpaceCreated"
    @close="isSpaceOpen = false"
  />
</template>

<style scoped>
.kb {
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 40px 64px;
}

.kb__page {
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 940px;
}

.kb__title {
  margin: 0;
  color: var(--color-text-primary);
  font-size: var(--font-size-h1);
  font-weight: var(--font-weight-semibold);
}

.kb__meta {
  margin: 0;
  color: var(--color-text-muted);
  font-size: var(--font-size-body-sm);
}

.kb__skeleton {
  height: 480px;
  max-width: 940px;
  border-radius: var(--radius-xl);
  background: var(--color-bg-muted);
}

</style>
